import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X } from 'lucide-react';
import { isNativeScanner, scanOnce } from '../utils/nativeScan';

const READER_ID = 'customer-pickup-qr-reader';

// Mirrors iOS CustomerPickupScanView: the customer scans the QR on the
// restaurant's receipt (or the restaurant's phone). The scan is only
// accepted when it matches THIS order — then onVerified() marks it picked up.
export default function CustomerPickupScanSheet({ open, onClose, orderId, orderNumber, restaurantId, restaurantName, onVerified }) {
  const [error, setError] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualId, setManualId] = useState('');
  const [verified, setVerified] = useState(false);
  const scannerRef = useRef(null);
  const doneRef = useRef(false);
  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  // Shared verification — used by both the native (Android) and browser
  // (web) scan paths.
  const verify = async (text) => {
    if (doneRef.current) return;
    let parsed = null;
    try { parsed = JSON.parse(text); } catch { /* not JSON */ }
    if (!parsed || parsed.type !== 'order_receipt' || !parsed.orderId) {
      setError("That isn't an order receipt QR code. Please scan the code on your receipt.");
      return;
    }
    if (parsed.orderId !== orderId) {
      setError(`This QR code is for a different order. Please scan the code for order #${orderNumber}.`);
      return;
    }
    if (parsed.restaurantId && restaurantId && parsed.restaurantId !== restaurantId) {
      setError('This QR code is from a different restaurant.');
      return;
    }
    doneRef.current = true;
    try {
      await scannerRef.current?.stop();
      scannerRef.current?.clear();
    } catch { /* already stopped */ }
    setVerified(true);
  };

  // Native path — full-screen ML Kit scanner (torch/zoom/close built in).
  // scanOnce returns null on cancel, so the user lands back on this sheet
  // and can scan again or enter the ID manually.
  async function runNativeScan() {
    setError('');
    const text = await scanOnce();
    if (text != null) await verify(text);
  }

  useEffect(() => {
    if (!open) return undefined;
    doneRef.current = false;
    setError('');
    setTorchOn(false);
    setTorchSupported(false);
    setManualMode(false);
    setManualId('');
    setVerified(false);

    // On Android the ML Kit scanner owns the camera — no html5 reader.
    if (isNativeScanner()) {
      runNativeScan();
      return () => {};
    }

    const scanner = new Html5Qrcode(READER_ID);
    scannerRef.current = scanner;

    scanner
      .start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 230, height: 230 } }, verify, () => {})
      .then(() => {
        // Torch support — Chrome/Android exposes it via the video track.
        try {
          const video = document.querySelector(`#${READER_ID} video`);
          const track = video?.srcObject?.getVideoTracks?.()[0];
          const caps = track?.getCapabilities?.();
          if (caps && 'torch' in caps) setTorchSupported(true);
        } catch { /* torch unsupported */ }
      })
      .catch(() => setError('Camera unavailable. Allow camera access and try again.'));

    return () => {
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        try {
          s.stop().then(() => s.clear()).catch(() => {});
        } catch {
          // stop() throws synchronously when the scanner never started
          try { s.clear(); } catch { /* noop */ }
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId, orderNumber, restaurantId]);

  // Camera torch (flash) — toggled through the running video track. Only
  // shown when the browser/device reports torch capability.
  async function toggleTorch() {
    try {
      const video = document.querySelector(`#${READER_ID} video`);
      const track = video?.srcObject?.getVideoTracks?.()[0];
      if (!track) return;
      const next = !torchOn;
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { /* torch failed — leave state as-is */ }
  }

  // The app shows the order as "ORD-XXXXXXXX" but the stored ID is the full
  // document ID — accept the full ID, the 8-char receipt number, or the
  // ORD- prefixed form (all case-insensitive).
  function matchesOrderId(entered) {
    let cleaned = (entered || '').trim().toLowerCase();
    if (cleaned.startsWith('ord-')) cleaned = cleaned.slice(4);
    else if (cleaned.startsWith('ord')) cleaned = cleaned.slice(3);
    const expected = (orderId || '').toLowerCase();
    return cleaned === expected || (cleaned.length >= 6 && expected.startsWith(cleaned));
  }

  async function submitManual() {
    if (doneRef.current) return;
    if (!matchesOrderId(manualId)) {
      setError('That order ID does not match this pickup. Please check the receipt and try again.');
      return;
    }
    doneRef.current = true;
    try {
      await scannerRef.current?.stop();
      scannerRef.current?.clear();
    } catch { /* already stopped */ }
    setVerified(true);
  }

  // OK on the success card — marks the order picked up + closes.
  function confirmPickup() {
    onVerifiedRef.current?.();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-3xl shadow-xl p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Scan to Confirm Pickup</h2>
            <p className="text-sm text-gray-500">
              Ask {restaurantName || 'the restaurant'} to show the QR code on your receipt, then scan it.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
            <X size={16} />
          </button>
        </div>

        <p className="text-center text-base font-bold text-gray-900">Order #{orderNumber}</p>

        {verified ? (
          <div className="space-y-4 py-6 text-center">
            <p className="text-5xl">🎉</p>
            <h3 className="text-xl font-bold text-gray-900">Order Picked Up! ✅</h3>
            <p className="text-sm text-gray-500">
              You've successfully confirmed your pickup — enjoy your meal! 🍔🎊
              Thanks for ordering with CheF SKN 🧡
            </p>
            <button
              onClick={confirmPickup}
              className="w-full rounded-xl bg-[#34C759] py-3 text-sm font-bold text-white"
            >
              OK
            </button>
          </div>
        ) : !manualMode ? (
          <>
            {isNativeScanner() ? (
              <button
                onClick={runNativeScan}
                className="w-full rounded-xl bg-black py-10 text-sm font-semibold text-white flex flex-col items-center gap-2"
              >
                <span className="text-3xl">📷</span>
                Tap to Scan QR Code
              </button>
            ) : (
              <div className="relative">
                <div id={READER_ID} className="w-full rounded-xl overflow-hidden bg-black min-h-[260px]" />
                {torchSupported && (
                <button
                  onClick={toggleTorch}
                  aria-label="Toggle flash"
                  className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-base ${
                    torchOn ? 'bg-yellow-400 text-black' : 'bg-black/55 text-white'
                  }`}
                >
                  {torchOn ? '🔦' : '⚡'}
                </button>
                )}
              </div>
            )}
            <button
              onClick={() => setManualMode(true)}
              className="w-full rounded-xl bg-blue-50 py-3 text-sm font-semibold text-blue-600"
            >
              Enter Order ID Manually
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <input
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="Order ID (e.g. ORD-W3IMeAJi)"
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FF6B35]/40"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setManualMode(false)}
                className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-semibold text-gray-600"
              >
                Back
              </button>
              <button
                onClick={submitManual}
                disabled={!manualId.trim()}
                className="flex-1 rounded-xl bg-[#FF6B35] py-2.5 text-sm font-bold text-white disabled:opacity-50"
              >
                Confirm
              </button>
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-500 text-center">{error}</p>}

        {!verified && (
          <button onClick={onClose} className="w-full py-3 rounded-lg bg-gray-100 text-gray-600 font-medium text-sm">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
